// G2 : types des réponses de l'API des inspections et requêtes partagées par les écrans.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { Brouillon, ContexteFormulaire, ModeleFormulaire, Reponses } from './modele';

export interface Libelle {
  code: string;
  libelle: string;
}

export interface ReferentielInspections {
  etats: Libelle[];
  natures: Libelle[];
  categoriesConstats: Libelle[];
  etatsConstats: Libelle[];
  formulaires: { id: string; code: string; version: number; titre: string; categories: string[] }[];
  produits: Record<string, string>;
}

export interface LigneInspection {
  id: string;
  reference: string;
  etat: string;
  nature: string;
  objet: string;
  datePrevue: string;
  provinceCode: string | null;
  installationId: string;
  installationNom: string;
  installationCategorie: string;
  organisationNom: string;
  inspecteurs: string[];
  transmiseLe: string | null;
  estDeMaMission: boolean;
}

export interface Suivi {
  id: string;
  action: string;
  commentaire: string;
  etatAvant: string;
  etatApres: string;
  auteur: string | null;
  creeLe: string;
}

export interface Constat {
  id: string;
  numero: number;
  categorie: string;
  description: string;
  correctionDemandee: string;
  delai: string | null;
  etat: string;
  source: string;
  creeLe: string;
  suivis: Suivi[];
}

export interface Piece {
  id: string;
  titre: string;
  categorie: string;
  nomFichier: string;
  taille: number;
  empreinteSha256: string;
  creeLe: string;
}

export interface DroitsInspection {
  saisir: boolean;
  demarrer: boolean;
  constater: boolean;
  examiner: boolean;
  cloturer: boolean;
  annuler: boolean;
  leverConstats: boolean;
  declarerCorrection: boolean;
}

export interface FicheInspectionDonnees {
  id: string;
  reference: string;
  etat: string;
  nature: string;
  objet: string;
  datePrevue: string;
  reponses: Reponses;
  versionReponses: number;
  debuteLe: string | null;
  transmiseLe: string | null;
  examineeLe: string | null;
  observationExamen: string | null;
  clotureeLe: string | null;
  motifCloture: string | null;
  motifAnnulation: string | null;
  rapportDocumentId: string | null;
  rapportEmpreinte: string | null;
  rapportValideLe: string | null;
  modifieLe: string | null;
  installation: { id: string; nom: string; categorie: string; adresse: string | null; provinceCode: string | null; organisationNom: string };
  formulaire: { id: string; code: string; version: number; titre: string; modele: ModeleFormulaire };
  contexte: ContexteFormulaire;
  equipe: { id: string; prenom: string; nom: string }[];
  constats: Constat[];
  pieces: Piece[];
  droits: DroitsInspection;
}

/** Mission du paquet de travail de l'inspecteur (consultable et saisissable hors connexion). */
export interface MissionPaquet {
  inspection: { id: string; reference: string; etat: string; objet: string; datePrevue: string; reponses: Reponses; versionReponses: number };
  installation: { nom: string; categorie: string; adresse: string | null; longitude: number; latitude: number; provinceCode: string | null } | undefined;
  formulaire: { id: string; version: number; titre: string; modele: ModeleFormulaire };
  contexte: ContexteFormulaire;
  constats: Omit<Constat, 'suivis'>[];
}

export interface PaquetInspecteur {
  genereLe: string;
  missions: MissionPaquet[];
  produits: Record<string, string>;
}

export interface ConstatSuivi {
  id: string;
  numero: number;
  categorie: string;
  description: string;
  correctionDemandee: string;
  delai: string | null;
  etat: string;
  inspectionId: string;
  inspectionReference: string;
  installationNom: string;
  provinceCode: string | null;
}

export function useReferentielInspections() {
  return useQuery({
    queryKey: ['inspections', 'referentiel'],
    queryFn: async () => (await api<ReferentielInspections>('/api/v1/inspections/referentiel')).donnees,
    staleTime: 5 * 60_000,
  });
}

export function useListeInspections(filtres: URLSearchParams = new URLSearchParams(), actif = true) {
  return useQuery({
    queryKey: ['inspections', 'liste', filtres.toString()],
    queryFn: async () => (await api<LigneInspection[]>(`/api/v1/inspections?${filtres.toString()}`)).donnees,
    enabled: actif,
  });
}

export function useConstatsASuivre(actif = true) {
  return useQuery({
    queryKey: ['inspections', 'constats-a-suivre'],
    queryFn: async () => (await api<ConstatSuivi[]>('/api/v1/constats?etat=a_suivre')).donnees,
    enabled: actif,
    retry: false,
  });
}

export const libelleDans = (liste: Libelle[] | undefined, code: string) => liste?.find((l) => l.code === code)?.libelle ?? code;

/** Étiquette d'état : la couleur ne signale que ce qui attend une action (exceptions d'abord). */
export function classeEtatInspection(etat: string): string {
  if (etat === 'transmise') return 'etiquette ambre';
  if (etat === 'annulee') return 'etiquette gris';
  return 'etiquette';
}

export function classeEtatConstat(etat: string, delaiDepasse: boolean): string {
  if (delaiDepasse && (etat === 'ouvert' || etat === 'correction_declaree')) return 'etiquette rouge';
  if (etat === 'correction_declaree') return 'etiquette ambre';
  if (etat === 'annule') return 'etiquette gris';
  return 'etiquette';
}

/** Date du jour au format AAAA-MM-JJ, heure du Gabon. */
export function aujourdhui(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Africa/Libreville', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export function delaiDepasse(c: { delai: string | null; etat: string }): boolean {
  return Boolean(c.delai && c.delai < aujourdhui() && (c.etat === 'ouvert' || c.etat === 'correction_declaree'));
}

export type { Brouillon };
